import { jsx, jsxs } from "react/jsx-runtime";
import { showContextMenu } from "@teselagen/ui";
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@blueprintjs/core";
import {
  chromatogramMenu,
  useShowChromQualScores
} from "../../utils/useChromatogramPrefs";
import classNames from "classnames";
export default function Chromatogram(props) {
  const { isRowView, chromatogramData, row, getGaps, charWidth } = props;
  const [showChromQualScores] = useShowChromQualScores();
  let [scalePct, setScalePct] = useState(0.05);
  const [hasDrawnOnce, setHasDrawnOnce] = useState(false);
  if (chromatogramData) {
    console.log(`Chromatogram comp - hasData: true, traceLen: ${chromatogramData.baseTraces?.length}, qualLen: ${chromatogramData.qualNums?.length}`);
  }
  if (props.scalePct) scalePct = props.scalePct;
  if (props.setScalePct) setScalePct = props.setScalePct;
  const canvasRef = useRef();
  const gapsBeforeRow = getGaps(row.start).gapsBefore;
  useEffect(() => {
    if (!chromatogramData || !chromatogramData.baseTraces || !canvasRef.current) {
      return true;
    }
    const painter = new drawTrace({
      isRowView,
      showChromQualScores,
      peakCanvas: canvasRef.current,
      traceData: chromatogramData,
      charWidth,
      startBp: row.start,
      endBp: row.end,
      getGaps,
      gapsBeforeRow,
      scalePct
    });
    painter.paintCanvas();
    setHasDrawnOnce(true);
  }, [
    setHasDrawnOnce,
    showChromQualScores,
    chromatogramData,
    charWidth,
    row.start,
    row.end,
    gapsBeforeRow,
    isRowView,
    scalePct,
    getGaps,
    canvasRef
  ]);
  const marginLeft = gapsBeforeRow * charWidth;
  if (chromatogramData.basePos && !chromatogramData.baseTraces) {
    throw new Error(
      'Chromatogram data is missing "baseTraces". Be sure to call the convertBasePosTraceToPerBpTrace(_chromData) before passing it to the editor'
    );
  }
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: classNames("chromatogram", {
        noQualityScores: !showChromQualScores
      }),
      style: {
        position: "relative",
        textAlign: "left"
      },
      onContextMenu: (e) => {
        showContextMenu([chromatogramMenu], void 0, e);
      },
      children: [
        /* @__PURE__ */ jsx(
          Button,
          {
            minimal: true,
            "data-tip": "Scale Chromatogram Up",
            className: "scaleChromatogramButtonUp",
            icon: "caret-up",
            onClick: (e) => {
              e.stopPropagation();
              setScalePct(scalePct + 0.01);
            },
            style: {
              zIndex: 10,
              position: "sticky",
              left: 145
            }
          }
        ),
        /* @__PURE__ */ jsx(
          Button,
          {
            minimal: true,
            "data-tip": "Scale Chromatogram Down",
            className: "scaleChromatogramButtonDown",
            icon: "caret-down",
            onClick: (e) => {
              e.stopPropagation();
              setScalePct(scalePct - 0.01);
            },
            style: {
              zIndex: 10,
              position: "sticky",
              left: 175
            }
          }
        ),
        /* @__PURE__ */ jsx("br", {}),
        /* @__PURE__ */ jsx(
          "div",
          {
            className: classNames({
              "chromatogram-trace": true,
              "chromatogram-trace-initialized": hasDrawnOnce
            }),
            style: {
              zIndex: 1,
              position: "relative",
              left: 0,
              // left: posOfSeqRead,
              display: "inline-block"
            },
            children: /* @__PURE__ */ jsx("canvas", { style: { marginLeft }, ref: canvasRef, height: "100" })
          }
        )
      ]
    }
  );
}
function drawTrace({
  traceData,
  charWidth,
  startBp,
  peakCanvas,
  endBp,
  getGaps,
  gapsBeforeRow,
  showChromQualScores,
  // isRowView,
  scalePct
}) {
  const colors = {
    adenine: "green",
    thymine: "red",
    guanine: "black",
    cytosine: "blue",
    other: "#ac68cc"
  };
  const ctx = peakCanvas.getContext("2d");
  const bottomBuffer = 0;
  const maxHeight = peakCanvas.height;
  const maxEndBpForTrace = Math.min(endBp, traceData.baseTraces.length - 1);
  const gapsBeforeEnd = getGaps(maxEndBpForTrace).gapsBefore;
  const seqLengthWithGaps = maxEndBpForTrace - startBp + 1 + gapsBeforeEnd - (gapsBeforeRow || 0);
  const maxWidth = Math.max(0, seqLengthWithGaps * charWidth);
  if (maxWidth > 3e4) {
    console.warn(`Chromatogram canvas width is very large: ${maxWidth}px. This might exceed browser limits and cause it to be invisible.`);
  }
  peakCanvas.width = maxWidth;
  const scaledHeight = maxHeight - bottomBuffer;
  this.drawPeaks = function(traceType, lineColor) {
    ctx.beginPath();
    const maxBaseIndex = Math.min(endBp, traceData.baseTraces.length - 1);
    for (let baseIndex = startBp; baseIndex <= maxBaseIndex; baseIndex++) {
      const traceForIndex = traceData.baseTraces[baseIndex]?.[traceType];
      if (!traceForIndex) continue;
      const gapsBefore = getGaps(baseIndex - 1).gapsBefore || 0;
      const gapsAt = getGaps(baseIndex).gapsBefore;
      const startXPosition = (baseIndex + gapsAt - startBp - gapsBeforeRow) * charWidth;
      const hasGaps = gapsBefore !== gapsAt;
      const traceLength = traceForIndex.length;
      const tracePointSpacing = charWidth / traceLength;
      traceForIndex.forEach((_tracePoint, tracePointIndex) => {
        const tracePoint = scaledHeight - scalePct * _tracePoint;
        if (hasGaps && tracePointIndex === 0) {
          ctx.moveTo(startXPosition, tracePoint);
        }
        ctx.lineTo(
          startXPosition + tracePointSpacing * tracePointIndex,
          tracePoint
        );
      });
    }
    ctx.strokeStyle = lineColor;
    ctx.stroke();
  };
  this.drawQualityScoreHistogram = function() {
    if (!traceData.qualNums || !showChromQualScores) return;
    const qualMax = Math.max(...traceData.qualNums);
    const scalePctQual = scaledHeight / qualMax;
    const maxBaseIndex = Math.min(endBp, traceData.qualNums.length - 1);
    for (let baseIndex = startBp; baseIndex <= maxBaseIndex; baseIndex++) {
      const gapsAt = getGaps(baseIndex).gapsBefore;
      const startXPosition = (baseIndex + gapsAt - startBp - gapsBeforeRow) * charWidth;
      ctx.rect(
        startXPosition,
        scaledHeight - traceData.qualNums[baseIndex] * scalePctQual,
        charWidth,
        traceData.qualNums[baseIndex] * scalePctQual
      );
    }
    ctx.fillStyle = "#cfc3c3";
    ctx.fill();
    ctx.strokeStyle = "#e9e3f4";
    ctx.stroke();
  };
  this.paintCanvas = function() {
    this.drawQualityScoreHistogram();
    this.drawPeaks("aTrace", colors.adenine);
    this.drawPeaks("tTrace", colors.thymine);
    this.drawPeaks("gTrace", colors.guanine);
    this.drawPeaks("cTrace", colors.cytosine);
    ctx.closePath();
  };
}
